import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCreateAdvertiser } from "@/hooks/use-advertisers";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";

export function AdvertiserCreatePage() {
  const navigate = useNavigate();
  const createMutation = useCreateAdvertiser();

  const [form, setForm] = useState({
    name: "",
    status: "active",
    defaultCurrencyId: "USD",
    platformName: "",
    platformUrl: "",
    attributionMethod: "last_touch",
    internalNotes: "",
  });

  const [error, setError] = useState("");

  function updateField(field: string, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    try {
      const result = await createMutation.mutateAsync(form);
      navigate({ to: "/advertisers/$id", params: { id: String(result.data.id) } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create advertiser");
    }
  }

  return (
    <div>
      <PageHeader title="Add Advertiser" description="Register a new advertiser account" backHref="/advertisers" />

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
        {error && (
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        <Card title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="e.g., Acme Corp"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => updateField("status", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={form.defaultCurrencyId}
                onChange={(e) => updateField("defaultCurrencyId", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attribution Method</label>
              <select
                value={form.attributionMethod}
                onChange={(e) => updateField("attributionMethod", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="last_touch">Last Touch</option>
                <option value="first_touch">First Touch</option>
              </select>
            </div>
          </div>
        </Card>

        <Card title="Platform">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Platform Name</label>
              <input
                type="text"
                value={form.platformName}
                onChange={(e) => updateField("platformName", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="e.g., Shopify"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Platform URL</label>
              <input
                type="url"
                value={form.platformUrl}
                onChange={(e) => updateField("platformUrl", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="https://shop.example.com"
              />
            </div>
          </div>
        </Card>

        <Card title="Internal Notes">
          <textarea
            value={form.internalNotes}
            onChange={(e) => updateField("internalNotes", e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder="Optional internal notes about this advertiser..."
          />
        </Card>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: "/advertisers" })}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? "Creating..." : "Add Advertiser"}
          </button>
        </div>
      </form>
    </div>
  );
}
